// Apple Device Enrollment (DEP) modelleri.
// Alan adları backend'in döndürdüğü C# property adlarıyla birebir (PascalCase).

/** Apple'ın orderType değerleri. RE/OV/VD yalnızca Index yetkili ekibinde. */
export type AppleDepOrderType = 'OR' | 'RE' | 'OV' | 'VD'

/** Cihaz ve gönderim statüleri. */
export type AppleDepStatus =
  | 'NEW'
  | 'WAITING_CUSTOMER_INFO'
  | 'READY'
  | 'SENT'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'PARTIAL_SUCCESS'
  | 'FAILED'
  | 'RETRY'
  | 'CANCELLED'
  | 'RETURNED'

export interface IAppleDepEnvironment {
  /** 1 = canlı, 0 = test. */
  IsLive: number
  TestEnvironment: string
  UseFakeClient: boolean
  OrderNumberSource: string
  BaseUrl: string
  HasCertificate: boolean
  ShipTo: string
  /** Index'in tek Apple Reseller ID'si (appsettings). Son kullanıcı bunu ABM'de yetkili tedarikçi olarak eklemeli. */
  DealerResellerId?: string
  /** Bayi bu ekranı kullanmaya yetkili mi (bayi kodu / APPLE DPP alt tipi)? */
  DealerHasAccess: boolean
  DealerCanSend: boolean
  DealerBlockReason?: string
  /** EndCustomer / DealerOwn / Both — cihaz hangi ABM hesabına kaydedilecek? */
  OrganizationIdMode: AppleDepOrganizationIdMode
  DealerOwnOrganizationId?: string
  DealerOwnOrganizationName?: string
  /** Mod kendi kuruma kayda izin veriyor mu? (ID henüz girilmemiş olsa bile) */
  DealerOwnAllowedByMode: boolean
  /** Atamada bayinin kendi kurumu seçilebilir mi? (mod izinli ve ID girilmiş) */
  CanUseDealerOwnOrganization: boolean
  /** Atamada son kullanıcı müşteri listesi kullanılabilir mi? */
  CanUseEndCustomerOrganization: boolean
}

export interface IAppleDepDeviceFilter {
  StartDate: string
  EndDate: string
  SerialNumber?: string
  InvoiceNo?: string
  Status?: string
  RefreshFromSap: boolean
}

export interface IAppleDepDevice {
  Id: number
  SerialNumber: string
  ProductCode?: string
  ProductDescription?: string
  SapInvoiceNo?: string
  SapDocNo?: string
  SapInvoiceDate?: string
  /** SAP SIPARIS_NO — Apple'a orderNumber olarak gider. */
  SapOrderNo?: string
  SapOrderDate?: string
  /** SAP TESLIMAT_NO — Apple'a deliveryNumber olarak gider. */
  SapDeliveryNo?: string
  SapDeliveryDate?: string
  DealerOrderNumber?: string
  DealerOrderDate?: string
  PoNumber?: string
  AssetTag?: string
  AppleDepCustomerId?: number
  CustomerName?: string
  OrganizationId?: string
  Status: AppleDepStatus
  StatusMessage?: string
  LastSentOrderType?: AppleDepOrderType
  EnrolledOn?: string
  CanSend: boolean
  BlockReason?: string
}

export interface IAppleDepCustomer {
  Id: number
  CustomerName: string
  /** Apple Business/School Manager Organization ID. */
  OrganizationId: string
  TaxNumber?: string
  ContactName?: string
  ContactEmail?: string
  ContactPhone?: string
  /** Bayinin kendi kurumu. Bayi kendi girer; Index admin de düzeltebilir. Silinemez. */
  IsDealerOwn?: boolean
  Active: boolean
  DeviceCount: number
}

/** Cihazın hangi ABM hesabına kaydedileceğini belirleyen mod. */
export type AppleDepOrganizationIdMode = 'EndCustomer' | 'DealerOwn' | 'Both'

export interface IAppleDepExcelImportRequest {
  CustomerName?: string
  OrganizationId?: string
  UseDealerOwnOrganization: boolean
  SalesOrderNumber?: string
  SalesOrderDate?: string
  PoNumber?: string
  TransactionType?: string
  SerialNumbers: string[]
  FileName?: string
  /** true ise hiçbir kayıt değişmez, yalnızca ne olacağı raporlanır. */
  ValidateOnly: boolean
}

export interface IAppleDepExcelImportRow {
  SerialNumber: string
  /** SAP cihaz havuzunda bulundu mu? */
  IsMatched: boolean
  /** Atama yapıldı mı (önizlemede: yapılabilir mi)? */
  IsAssigned: boolean
  DeviceId?: number
  ProductDescription?: string
  SapOrderNo?: string
  Status?: string
  Message?: string
}

export interface IAppleDepExcelImportResult {
  ValidateOnly: boolean
  CustomerName?: string
  OrganizationId?: string
  CustomerCreated: boolean
  TotalRows: number
  MatchedCount: number
  AssignedCount: number
  SkippedCount: number
  Rows: IAppleDepExcelImportRow[]
}

export interface IAppleDepAssignCustomerRequest {
  DeviceIds: number[]
  AppleDepCustomerId: number
  /** true ise cihazlar bayinin kendi ABM hesabına kaydedilir. */
  UseDealerOwnOrganization: boolean
  DealerOrderNumber?: string
  DealerOrderDate?: string
  PoNumber?: string
  AssetTag?: string
}

export interface IAppleDepAssignCustomerResult {
  UpdatedCount: number
  Message: string
  BlockedDevices: string[]
}

export interface IAppleDepSendRequest {
  DeviceIds: number[]
  OrderType: AppleDepOrderType
}

export interface IAppleDepSkippedDevice {
  DeviceId: number
  SerialNumber: string
  Reason: string
}

export interface IAppleDepSendResult {
  TransactionRowId: number
  TransactionId: string
  DeviceEnrollmentTransactionId?: string
  Status: AppleDepStatus
  StatusCode?: string
  StatusMessage?: string
  ErrorCode?: string
  ErrorMessage?: string
  SentDeviceCount: number
  IsLive: boolean
  IsFake: boolean
  SkippedDevices: IAppleDepSkippedDevice[]
}

export interface IAppleDepTransactionFilter {
  StartDate: string
  EndDate: string
  Status?: string
  OrderType?: string
  TransactionId?: string
  OnlyEscalated?: boolean
}

export interface IAppleDepTransactionDevice {
  Id: number
  AppleDepDeviceId: number
  SerialNumber: string
  OrderNumber: string
  OrderDate: string
  DeliveryNumber?: string
  ShipDate?: string
  OrganizationId: string
  PoNumber?: string
  AssetTag?: string
  DevicePostStatus?: string
  DevicePostStatusMessage?: string
  OrderPostStatus?: string
  DeliveryPostStatus?: string
  IsFailed: boolean
}

export interface IAppleDepTransaction {
  Id: number
  DealerCode: string
  DealerName?: string
  TransactionId: string
  DeviceEnrollmentTransactionId?: string
  DepResellerId: string
  OrderType: AppleDepOrderType
  Status: AppleDepStatus
  StatusCode?: string
  StatusMessage?: string
  ErrorCode?: string
  ErrorMessage?: string
  DeviceCount: number
  IsLive: boolean
  IsFake: boolean
  SentAt: string
  SentByAdmin: boolean
  CompletedOn?: string
  LastCheckedAt?: string
  CheckCount: number
  EscalatedOn?: string
  FailedDeviceCount: number
  Devices: IAppleDepTransactionDevice[]
}

/** Statü kodlarının Türkçe karşılıkları — backend ile aynı sözlük. */
export const AppleDepStatusLabels: Record<string, string> = {
  NEW: 'Yeni',
  WAITING_CUSTOMER_INFO: 'Müşteri bilgisi bekliyor',
  READY: 'Gönderime hazır',
  SENT: "Apple'a gönderildi",
  PROCESSING: "Apple'da işlemde",
  SUCCESS: 'Kayıt başarılı',
  PARTIAL_SUCCESS: 'Kısmen başarılı',
  FAILED: 'Başarısız',
  RETRY: 'Yeniden gönderim bekliyor',
  CANCELLED: 'İptal edildi',
  RETURNED: 'İade edildi',
}

export const AppleDepOrderTypeLabels: Record<string, string> = {
  OR: 'Kayıt (OR)',
  RE: 'İade (RE)',
  OV: 'Düzeltme (OV)',
  VD: 'İptal (VD)',
}

/** Statüye göre bootstrap renk sınıfı. */
export const appleDepStatusClass = (status?: string): string => {
  switch (status) {
    case 'SUCCESS':
      return 'appleDepBadge appleDepBadgeSuccess'
    case 'PARTIAL_SUCCESS':
      return 'appleDepBadge appleDepBadgeWarning'
    case 'FAILED':
    case 'CANCELLED':
      return 'appleDepBadge appleDepBadgeDanger'
    case 'SENT':
    case 'PROCESSING':
      return 'appleDepBadge appleDepBadgeInfo'
    case 'READY':
      return 'appleDepBadge appleDepBadgeReady'
    case 'RETURNED':
      return 'appleDepBadge appleDepBadgeReturned'
    default:
      return 'appleDepBadge appleDepBadgeDefault'
  }
}
